/**
 * Populates if a DOM element is in Viewport
 * @param {object} DOM element
 * @param {boolean} should bottom part of the DOM element be skipped during the test? Default is true
 * @return {boolean}
 */
function isInViewport(element, skipBottom = true) {
  const elementRect = element.getBoundingClientRect();
  const outcome =
    elementRect.left >= 0 &&
    elementRect.right <=
      (window.innerWidth || document.documentElement.clientWidth) &&
    elementRect.top >= 0;
  if (skipBottom) {
    return outcome;
  }
  return (
    outcome &&
    (parseInt(element.style.bottom, 10) || elementRect.bottom) <=
      (window.innerHeight || document.documentElement.clientHeight)
  );
}

/**
 * Populates if a DOM element is overlapped by other element
 * @param {object} DOM element
 * @return {boolean}
 */
function isOverlapped(element) {
  const elementRect = element.getBoundingClientRect();
  const topElement = document.elementFromPoint(
    elementRect.left,
    elementRect.top
  );
  if (!topElement) {
    return false;
  }

  if (element !== topElement && element.contains(topElement)) {
    return false;
  }
  return !element.isSameNode(topElement);
}

/**
 * Populates if target element is introducing a scroll in the Viewport
 * @param {object} Host DOM element
 * @param {object} Target DOM element
 * @return {boolean}
 */
function isTargetIntroducingScroll(host, target) {
  const hostElement = host;
  const menuElement = target;
  const hostElementRect = hostElement.getBoundingClientRect();

  // Test condition: If target is hidden and hostElement coordinates don't change, then no scroll is getting introduced
  menuElement.style.display = 'none';
  const hostElementRectAfter = hostElement.getBoundingClientRect();

  if (
    hostElementRect.left === hostElementRectAfter.left &&
    hostElementRect.right === hostElementRectAfter.right
  ) {
    menuElement.style.display = '';
    return false;
  }

  menuElement.style.display = '';
  return true;
}

/**
 *  Populates Menu Alignment position for menuElement (relative to hostElement) based on an algorithm
 *  This algorithm assumes menuElement is 'absolute' positioned and adjacent to hostElement.
 *  Example: <div><hostElement /><menuElement style="position: absolute;" /></div>
    Solution: Cases in order -
      1.  Set and Test left alignment for menuElement if menu is positioned outside the left viewport
      2.  Set and Test right alignment for menuElement if menu is positioned outside the right viewport
      3.  Set center alignment for menuElement if above two cases fail
 * @param {object} Host DOM element i.e. Tab Menu, Icon Menu, Menu Button
 * @param {object} Target DOM element i.e. Menu
 * @return {'left/right': [number]px}
 */
function getMenuAlignment(host, menu) {
  const hostElement = host; // Clone element to avoid sliding effect?
  const menuElement = menu; // Clone element to avoid sliding effect?
  const hostElementRect = hostElement.getBoundingClientRect();
  const menuElementRect = menuElement.getBoundingClientRect();
  const menuElementLeft = menuElementRect.left;
  const menuElementRight = menuElementRect.right;
  const windowWidth = window.innerWidth;
  const hostElementWidth = hostElementRect.width;
  const menuElementWidth = menuElementRect.width;

  if (
    menuElementLeft > 0 &&
    menuElementRight <= windowWidth &&
    !isTargetIntroducingScroll(hostElement, menuElement) &&
    isInViewport(menuElement) &&
    !isOverlapped(menuElement)
  ) {
    return null;
  }

  if (menuElementLeft < 0) {
    menuElement.style.left = '0px';
    menuElement.style.right = 'auto';
    if (
      !isTargetIntroducingScroll(hostElement, menuElement) &&
      isInViewport(menuElement) &&
      !isOverlapped(menuElement)
    ) {
      return { left: '0px' };
    }
  } else if (menuElementRight >= windowWidth) {
    menuElement.style.left = 'auto';
    menuElement.style.right = '0px';
    if (
      !isTargetIntroducingScroll(hostElement, menuElement) &&
      isInViewport(menuElement) &&
      !isOverlapped(menuElement)
    ) {
      return { right: '0px' };
    }
  }

  const leftPosition = (menuElementWidth - hostElementWidth) / 2;
  menuElement.style.left = `${leftPosition}px`;
  menuElement.style.right = 'auto';
  return { left: `${leftPosition < 0 ? leftPosition : leftPosition * -1}px` };
}

// Handle Listbox position in Table
function placeListboxInTable(tableId) {
  const table = document.getElementById(tableId);
  // search for ul tag to optimize query and do nothing if no ul present
  const ul = table?.classList.contains('sticky') ? table?.querySelector('tbody ul') : table?.querySelector('ul');

  if (!ul) {
    return;
  }
  // Find listbox wrapper
  const listbox = ul?.closest('div[data-test$="-cybsMenu"]').parentElement;
  // Find anchor element
  const anchor = listbox?.closest(
    'div[data-test$="-cybsMenuButton"], div[data-test$="-cybsCombobox"]'
  );

  // Defensive code to avoid crash if component implementation changes
  if (!anchor || !listbox) {
    return;
  }

  // Get rect params
  const tableRect = table.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const scroller = table.parentNode;

  // Do math to calculate listbox horizontal offset
  // Math.max used to keep listbox from running off left side of screen
  listbox.style.left = `${Math.max(
    0,
    anchorRect.left - scroller.scrollLeft - tableRect.left
  )}px`;
  listbox.style.top = `${
    anchorRect.bottom - scroller.scrollTop - tableRect.top
  }px`;
}

export { getMenuAlignment, placeListboxInTable };
